import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DraftProvider, useDraft } from '../contexts/DraftContext';
import DraftSetup from './DraftSetup';
import CommissionerDraftView from './CommissionerDraftView';
import DraftControls from '../components/DraftControls';
import DraftBoard from '../components/DraftBoard';
import AvailablePlayers from '../components/AvailablePlayers';
import TeamRoster from '../components/TeamRoster';

/**
 * Root draft route component. Wraps DraftProvider with the URL draftId (if any)
 * and renders the inner view once the context is ready.
 */
function DraftView() {
  const { draftId } = useParams();
  return (
    <DraftProvider draftId={draftId} forceNew={!draftId}>
      <DraftViewInner urlDraftId={draftId} />
    </DraftProvider>
  );
}

function DraftViewInner({ urlDraftId }) {
  const { isLoggedIn, isLoading: authLoading } = useAuth();
  const { draft, loading } = useDraft();
  const navigate = useNavigate();

  if (authLoading || loading) {
    return (
      <div className='loading'>
        <div className='spinner' />
        <p>Loading draft...</p>
      </div>
    );
  }

  // /draft/:id — show the specific draft (read-only if completed or not owner)
  if (urlDraftId) {
    if (!draft) {
      return <div className='error-banner'>Draft not found.</div>;
    }
    return <DraftBoardOrCommissionerView />;
  }

  // /draft/new — anonymous users get a sign-in prompt
  if (!isLoggedIn) {
    return (
      <div className='card' style={{ maxWidth: 480, margin: '48px auto', textAlign: 'center' }}>
        <h2>My Drafts</h2>
        <p style={{ color: 'var(--text-muted)', marginBottom: 24 }}>
          Create a free account to start and save your draft progress.
        </p>
        <button
          className='btn btn-primary'
          onClick={() => navigate('/login?returnTo=/draft/new')}
        >
          Sign in to start drafting
        </button>
      </div>
    );
  }

  // Logged-in user with no active draft → show setup form
  if (!draft) {
    return <DraftSetup />;
  }

  // Active draft found (auto-resumed from server or localStorage)
  return <DraftBoardOrCommissionerView />;
}

// Commissioner Mode drafts get the mobile-first CommissionerDraftView instead of the desktop
// three-panel DraftBoardLayout below, while the draft is still in progress -- solo Draft Mode and
// Mock Draft (commissionerMode: false) render DraftBoardLayout exactly as before this branch
// existed, completely unchanged. A *completed* Commissioner Mode draft falls through to the same
// DraftBoardLayout every other draft flavor uses for review: it already handles a finished draft
// correctly (full board, real team names, roster panel), and — unlike CommissionerDraftView's
// compact recent-picks strip / TV Display — is the only place opponent-profile linking exists
// (DraftBoard.jsx, commissioner-only, allowed for a completed Commissioner Mode draft since the
// live-draft "no path to pick" restriction no longer applies once picking is over).
function DraftBoardOrCommissionerView() {
  const { draft } = useDraft();
  const showCommissionerView = draft.commissionerMode && draft.status !== 'completed';
  return showCommissionerView ? <CommissionerDraftView /> : <DraftBoardLayout />;
}

function DraftBoardLayout() {
  const { error, clearError } = useDraft();
  return (
    <div className='draft-layout'>
      <DraftControls />
      {error && (
        <div className='error-banner error-banner--dismissable' style={{ margin: 0 }}>
          {error}
          <button className='error-banner__dismiss' onClick={clearError} aria-label='Dismiss'>✕</button>
        </div>
      )}
      <div className='draft-board-area'>
        <DraftBoard />
      </div>
      <div className='draft-bottom-panels'>
        <div className='draft-available-panel'>
          <AvailablePlayers />
        </div>
        <div className='draft-roster-panel'>
          <TeamRoster />
        </div>
      </div>
    </div>
  );
}

export default DraftView;
